import { TabContext, TabPanel } from "@mui/lab";
import { useEffect, useMemo, useState } from "react";
import { SxProps } from "@mui/material";
import Box from "@mui/material/Box";
import { useSearchParams } from "react-router-dom";
import qs from "qs";
import TabList from "./components/TabList";
import { Tab } from "./types";

export type TabLayoutProps = {
  tabs: Tab[];
  defaultTab: string;
  sx?: SxProps;
  withSearchParams?: boolean;
};

const TabLayout = ({ tabs, defaultTab, sx, withSearchParams = false }: TabLayoutProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => qs.parse(searchParams.toString()), [searchParams]);
  const { tab: tabParam } = params as {
    tab?: string;
    scrollTop?: string;
  };

  const getInitialTab = (): string => {
    if (!withSearchParams) return defaultTab;

    const initTabValue = tabs.find((tab) => tab.id === tabParam);
    if (initTabValue) return initTabValue.id;

    return defaultTab;
  };

  const [tabValue, setTab] = useState<string>(getInitialTab());

  useEffect(() => {
    if (withSearchParams && tabParam !== tabValue) {
      const { scrollTop, ...restParams } = params;
      setSearchParams(
        qs.stringify({
          ...restParams,
          tab: tabValue,
        }),
        { replace: true }
      );
    }
  }, [tabValue]);

  useEffect(() => {
    if (tabParam && tabParam !== tabValue) setTab(tabParam);
  }, [searchParams]);

  return (
    <Box sx={sx}>
      <TabContext value={tabValue}>
        <TabList
          tabs={tabs}
          onChange={(newTabId) => setTab(newTabId)}
        />
        {tabs.map((tabPanel) => (
          <TabPanel
            key={tabPanel.id}
            value={tabPanel.id}
            sx={{
              p: 0,
              width: "100%",
              "& > div": {
                backgroundColor: "white",
                borderRadius: 2,
                px: 2.5,
                width: "100%",
              },
              ...tabPanel.sx,
            }}>
            {tabPanel.component}
          </TabPanel>
        ))}
      </TabContext>
    </Box>
  );
};

export default TabLayout;
